import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage & Cluster-HUB „Verkaufsautomat kaufen“
 * (/verkaufsautomat). Kopf-Keyword „verkaufsautomat“ (~1.750/Mon) + Zweit-
 * Keyword „verkaufsautomat hersteller“ (~90/Mon, hoher CPC) über den
 * „Hersteller aus Kiel / Made in Germany“-Abschnitt.
 *
 * Besonderer Zweck: interne Verlinkung in ALLE Segment-Landingpages
 * (eier, brot, obst, wein, blumen, honig, bier, waren, hofladen, …) – der
 * zentrale Hub, der den Themen-Cluster zusammenbindet. Modifier
 * kaufen/mieten/outdoor als Anker-Abschnitte (keine eigenen dünnen Seiten).
 */
@Component({
  selector: 'app-verkaufsautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './verkaufsautomat-page.component.html',
  styleUrls: ['../cluster-landing.shared.scss'],
})
export class VerkaufsautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSmart = 'was-macht-smarte-verkaufsautomaten-aus';
  readonly blogGeld = 'mit-automaten-geld-verdienen';
  readonly blogKosten = 'laufende-kosten-von-automaten';

  /** Segment-Kacheln = interne Verlinkung in den ganzen Cluster (SEO-Hub). */
  readonly segments: { slug: string; label: string; icon: string }[] = [
    { slug: 'brotautomat', label: $localize`:@@va.seg.brot:Brot & Backwaren`, icon: '🥖' },
    { slug: 'eierautomat', label: $localize`:@@va.seg.eier:Eier`, icon: '🥚' },
    { slug: 'obstautomat', label: $localize`:@@va.seg.obst:Obst & Gemüse`, icon: '🍎' },
    { slug: 'weinautomat', label: $localize`:@@va.seg.wein:Wein`, icon: '🍷' },
    { slug: 'bierautomat', label: $localize`:@@va.seg.bier:Bier`, icon: '🍺' },
    { slug: 'honigautomat', label: $localize`:@@va.seg.honig:Honig`, icon: '🍯' },
    { slug: 'blumenautomat', label: $localize`:@@va.seg.blumen:Blumen`, icon: '💐' },
    { slug: 'kartoffelautomat', label: $localize`:@@va.seg.kartoffel:Kartoffeln`, icon: '🥔' },
    { slug: 'hofladen-automat', label: $localize`:@@va.seg.hofladen:Hofladen`, icon: '🚜' },
    { slug: 'lebensmittelautomat', label: $localize`:@@va.seg.lebensmittel:Lebensmittel`, icon: '🧺' },
    { slug: 'warenautomat', label: $localize`:@@va.seg.waren:Non-Food & Waren`, icon: '🎁' },
    { slug: 'automatenladen', label: $localize`:@@va.seg.aladen:Automatenladen`, icon: '🏪' },
  ];

  /** Live-Beispiel: Hofbäckerei Hömberg. */
  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/hofbaeckerei-hoemberg/U-MB-00035'
  );

  /** YouTube-Facade (lädt erst nach Klick): Produktion in Kiel. */
  readonly videoThumb = 'https://i.ytimg.com/vi/XIZETmlGIoo/hqdefault.jpg';
  videoOpen = false;
  videoUrl?: SafeResourceUrl;

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@va.faq1.q:Was kostet ein Verkaufsautomat?`,
      a: $localize`:@@va.faq1.a:Die marktbox gibt es ab 6.800 € zum Kauf oder ab 300 € pro Monat zur Miete – je nach Modell (24 oder 39 Fächer) und Zubehör.`,
    },
    {
      q: $localize`:@@va.faq2.q:Verkaufsautomat kaufen oder mieten?`,
      a: $localize`:@@va.faq2.a:Beides ist möglich. Kaufen lohnt sich im Dauerbetrieb, Mieten senkt die Einstiegshürde und macht die Kosten planbar. Du kannst später jederzeit erweitern.`,
    },
    {
      q: $localize`:@@va.faq3.q:Kann der Verkaufsautomat draußen stehen?`,
      a: $localize`:@@va.faq3.a:Ja. Die marktbox ist wetterfest und outdoor-tauglich – am Feldrand, am Hoftor, vor dem Laden oder an gut sichtbaren Standorten mit Laufkundschaft.`,
    },
    {
      q: $localize`:@@va.faq4.q:Von welchem Hersteller ist die marktbox?`,
      a: $localize`:@@va.faq4.a:Die marktbox wird von der marktbox GmbH in Kiel entwickelt und produziert – Made in Germany. Die GmbH besteht seit 2019, die marktbox wird seit 2021 hergestellt und vertrieben.`,
    },
    {
      q: $localize`:@@va.faq5.q:Was kann ich in einem Verkaufsautomaten verkaufen?`,
      a: $localize`:@@va.faq5.a:Alle ungekühlten Produkte: Backwaren, Eier, lagerfähiges Obst & Gemüse, Honig, Wein, Flaschenbier sowie Non-Food und Geschenkartikel. Eine Kühlung ist derzeit nicht vorgesehen.`,
    },
  ];

  openVideo(): void {
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube-nocookie.com/embed/XIZETmlGIoo?autoplay=1&rel=0'
    );
    this.videoOpen = true;
  }

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@va.metaTitle:Verkaufsautomat kaufen & mieten – Hersteller | marktbox`;
    const description =
      $localize`:@@va.metaDesc:Verkaufsautomat kaufen oder mieten – direkt vom Hersteller. Die marktbox verkauft 24/7, bargeldlos & wetterfest. Ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content:
        $localize`:@@va.metaKeywords:Verkaufsautomat, Verkaufsautomat kaufen, Verkaufsautomat mieten, Verkaufsautomat aufstellen, Verkaufsautomat Hersteller, Verkaufsautomat outdoor, automat kaufen, automat aufstellen, vending automat, Warenautomat, Made in Germany, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('verkaufsautomat-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Verkaufsautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      manufacturer: { '@type': 'Organization', name: 'marktbox GmbH', address: { '@type': 'PostalAddress', addressLocality: 'Kiel', addressCountry: 'DE' } },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('verkaufsautomat-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    this.injectJsonLd('verkaufsautomat-video-schema', {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: 'marktbox – Produktion in Kiel',
      description:
        'Blick in die Produktion der marktbox GmbH in Kiel: Wie der Verkaufsautomat „Made in Germany“ entsteht und wer dahintersteht.',
      thumbnailUrl: 'https://i.ytimg.com/vi/XIZETmlGIoo/hqdefault.jpg',
      uploadDate: '2024-06-17',
      duration: 'PT2M18S',
      embedUrl: 'https://www.youtube-nocookie.com/embed/XIZETmlGIoo',
      contentUrl: 'https://www.youtube.com/watch?v=XIZETmlGIoo',
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/verkaufsautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
